/**
 * Remote Logger - Sends console logs to server
 */

class RemoteLogger {
  constructor() {
    this.logs = [];
    this.maxBatchSize = 10;
    this.flushInterval = 2000; // 2 seconds
    this.endpoint = '/api/log';
    this.sessionId = this._generateSessionId();

    // Start periodic flush
    setInterval(() => this.flush(), this.flushInterval);

    // Flush on page unload
    window.addEventListener('beforeunload', () => this.flush(true));
  }

  _generateSessionId() {
    return Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  log(level, args) {
    const entry = {
      timestamp: new Date().toISOString(),
      level: level,
      message: args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch (e) {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' '),
      userAgent: navigator.userAgent,
      url: window.location.href,
      sessionId: this.sessionId
    };

    this.logs.push(entry);

    if (this.logs.length >= this.maxBatchSize) {
      this.flush();
    }
  }

  flush(sync = false) {
    if (this.logs.length === 0) return;

    const logsToSend = [...this.logs];
    this.logs = [];

    const payload = JSON.stringify(logsToSend);

    if (sync && navigator.sendBeacon) {
      // Use sendBeacon for synchronous sending on page unload
      navigator.sendBeacon(this.endpoint, payload);
    } else {
      // Use fetch for normal logging
      fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(err => {
        // Silently fail - don't want to break the app
        console.error('Remote logging failed:', err);
      });
    }
  }
}

// Initialize and intercept console methods
let remoteLogger = null;

export function initRemoteLogging() {
  if (remoteLogger) return; // Already initialized

  remoteLogger = new RemoteLogger();

  // Store original console methods
  const originalLog = console.log;
  const originalError = console.error;
  const originalWarn = console.warn;
  const originalInfo = console.info;

  // Intercept console.log
  console.log = function(...args) {
    originalLog.apply(console, args);
    remoteLogger.log('log', args);
  };

  // Intercept console.error
  console.error = function(...args) {
    originalError.apply(console, args);
    remoteLogger.log('error', args);
  };

  // Intercept console.warn
  console.warn = function(...args) {
    originalWarn.apply(console, args);
    remoteLogger.log('warn', args);
  };

  // Intercept console.info
  console.info = function(...args) {
    originalInfo.apply(console, args);
    remoteLogger.log('info', args);
  };

  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    remoteLogger.log('error', [
      'Uncaught error:',
      event.message,
      'at',
      event.filename + ':' + event.lineno + ':' + event.colno
    ]);
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    remoteLogger.log('error', [
      'Unhandled promise rejection:',
      event.reason
    ]);
  });

  console.log('[RemoteLogger] Initialized - session:', remoteLogger.sessionId);
}
